import { createFileRoute, useRouter } from '@tanstack/react-router'
import { VaultFiles } from '../components/VaultFiles.tsx'
import { ApiError } from '../lib/api-fetch.ts'
import { fetchHierarchy } from '../lib/hierarchy-api.ts'
import { withSessionRedirect } from '../lib/require-session.ts'
import {
  getTenantKey,
  listPrincipals,
  listVaultFiles,
} from '../lib/vault-api.ts'
import type { VaultFileSummary } from '../lib/vault-api.ts'

/**
 * `/vault/files`.
 *
 * The loader tolerates the files API not existing. The server side
 * (`vault_files` + `vault_file_chunks`, per D1/D3) is in-tree, but the
 * admin app and the API deploy separately, so a 404 on
 * `/vault/admin/…/files` means "not deployed here", not "something is
 * wrong" — it becomes `files: null` and the page says so in one
 * sentence. A thrown 404 would take the whole route to the error
 * boundary and make a deploy skew look like a regression.
 *
 * ONLY a 404 is tolerated. A 401 still bounces to /login through
 * `withSessionRedirect`, and a 403 or a 500 still reaches the error
 * boundary — those are real and the operator should see them.
 */
export const Route = createFileRoute('/vault/files')({
  loader: async () =>
    withSessionRedirect(async () => {
      const tree = await fetchHierarchy()
      const rootId = tree.roots[0]?.id ?? ''
      if (rootId === '')
        return { rootId, files: null, principals: [], tenantPubkey: null }
      const [tenantKey, principals, files] = await Promise.all([
        getTenantKey(rootId),
        listPrincipals(rootId),
        listFilesIfDeployed(rootId),
      ])
      return {
        rootId,
        files,
        principals: principals.filter(
          (principal) => principal.kind !== 'agent_defaults',
        ),
        tenantPubkey: tenantKey.tenantPubkey,
      }
    }),
  component: VaultFilesRoute,
})

/** `null` for "this endpoint is not here yet". Nothing is swallowed: a
 * 404 is a modelled answer the page renders, and every other failure is
 * rethrown untouched. */
async function listFilesIfDeployed(
  rootId: string,
): Promise<VaultFileSummary[] | null> {
  try {
    return await listVaultFiles(rootId, { limit: 100 })
  } catch (cause) {
    if (cause instanceof ApiError && cause.status === 404) return null
    throw cause
  }
}

function VaultFilesRoute() {
  const data = Route.useLoaderData()
  const router = useRouter()

  return (
    <VaultFiles
      rootId={data.rootId}
      files={data.files}
      recipients={data.principals}
      tenantPubkey={data.tenantPubkey}
      onChanged={async () => {
        await router.invalidate()
      }}
    />
  )
}
